import { useTranslation } from 'react-i18next';
import { Box, Divider, HStack, SectionList, Spacer, VStack } from 'native-base';
import { map, startCase, toLower } from 'lodash';

import { Text } from '@pimm/base';
import { formatToTimeOnly } from '@app/utils/date-formatter';
import { DayBlock } from '@app/features/store-core';
import { useAppLocale } from '@pimm/common';

type DailyStaffReviewListProps = {
  dayBlock: DayBlock;
  bgColor?: string;
  color?: string;
  sections: {
    title: string;
    bgColor: string | undefined;
    color: string | undefined;
    data: {
      name: string;
      startTime: Date | undefined;
      endTime: Date | undefined;
    }[][];
  }[];
};

export const DailyStaffReviewList = ({ dayBlock, sections }: DailyStaffReviewListProps) => {
  const { t } = useTranslation();
  const { translate } = useAppLocale();
  return (
    <Box h="full">
      <VStack space={1} rounded="xl" p={{ md: 2, xl: 2.5 }} maxHeight="100%" borderWidth={1} bg="gray.50">
        <HStack justifyContent="space-between" pb={2}>
          <Box flex={1} justifyContent="flex-start" pr={1}>
            <Box justifyContent="center" mb={1} h={5}>
              <Text size="lg" fontWeight={600} color="gray.900" lineHeight="xs">
                {translate(dayBlock.title, dayBlock.titleTranslations)}
              </Text>
            </Box>
            <Text size="md" fontWeight={400} color="gray.600" lineHeight="xs">
              {translate(dayBlock.subTitle, dayBlock.subTitleTranslations)}
            </Text>
          </Box>

          <Box alignItems="flex-end">
            <Box justifyContent="center" mb={2} w="75px" h={5} borderRadius="sm" bg="white">
              <Text size="lg" fontWeight={600} color="gray.900" lineHeight="xs" textAlign="center">
                {formatToTimeOnly(dayBlock.staffStartTime ?? dayBlock.startTime)}
              </Text>
            </Box>

            <HStack
              space={2}
              alignItems="center"
              justifyContent="space-between"
              rounded="full"
              pl={2}
              pr={1}
              h={7}
              borderWidth={1}
              borderColor="blueLight.700"
              bg="blueLight.200"
            >
              <Text size="sm" fontWeight={500} color="blueLight.700" lineHeight="xs">
                {t('positioning_plan:starting_staff')}
              </Text>

              <Box alignItems="center" justifyContent="center" rounded="full" w={5} h={5} bg="white">
                <Text size="sm" fontWeight={500} color="blueLight.700" lineHeight="xs">
                  {sections[0].data[0].length}
                </Text>
              </Box>
            </HStack>
          </Box>
        </HStack>

        <Box flex={1}>
          <SectionList
            borderRadius="lg"
            borderWidth={1}
            borderColor="gray.200"
            overflow="hidden"
            removeClippedSubviews
            scrollEnabled
            stickyHeaderIndices={[0]}
            sections={sections}
            ListHeaderComponent={() => (
              <HStack alignItems="center" pl={2.5} pr={2} h={7} bg="gray.200">
                <Box flex={1}>
                  <Text size="sm" fontWeight={500} color="gray.900">
                    {t('positioning_plan:name')}
                  </Text>
                </Box>
                <HStack flex={1} space={1} alignItems="center" justifyContent="flex-end" w="70px">
                  <Text size="sm" fontWeight={500} color="gray.900">
                    {t('positioning_plan:shift_start')}
                  </Text>
                </HStack>
              </HStack>
            )}
            renderSectionHeader={({ section }) => (
              <HStack alignItems="center" pl={2.5} h={5} bg={section.bgColor ?? 'gray.600'}>
                <Text size="sm" fontWeight={600} color={section.color ?? 'white'}>
                  {section.title}
                </Text>
              </HStack>
            )}
            renderItem={({ item: staffs, section }) => {
              return (
                <VStack bg={section.bgColor ? 'blueLight.50' : 'white'}>
                  {/* Display empty placeholder */}
                  {staffs.length === 0 && (
                    <Box justifyContent="center" minH={6}>
                      <Text size="sm" fontWeight={500} color="gray.500" textAlign="center">
                        {`${t('positioning_plan:no_employees')} ${toLower(t(section.title))}`}
                      </Text>
                    </Box>
                  )}
                  {/* Render each staff information */}
                  {map(staffs, (staff, index) => (
                    <HStack key={`${section.title}:${index}`} alignItems="center" pl={2.5} minH={7} borderTopWidth={index ? 1 : 0}>
                      <Text size="sm" fontWeight={500} color="gray.900" lineHeight="sm" numberOfLines={1} ellipsizeMode="tail">
                        {startCase(toLower(staff.name))}
                      </Text>
                      <Spacer />
                      <Divider orientation="vertical" bg="gray.200" />
                      <Box alignItems="center" w="70px">
                        <Text size="sm" fontWeight={500} color="gray.900">
                          {formatToTimeOnly(staff.startTime)}
                        </Text>
                      </Box>
                    </HStack>
                  ))}
                </VStack>
              );
            }}
          />
        </Box>
      </VStack>
    </Box>
  );
};
